import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../redux/store';
import SideNavbar from '../../components/SideNavbar';
import { db } from '../../firebaseConfig';
import Toast from '../../components/Toast';
import { 
    collection, 
    doc, 
    setDoc, 
    getDoc, 
    getDocs, 
    deleteDoc, 
    query, 
    where, 
    serverTimestamp, 
    Timestamp,
    writeBatch
} from 'firebase/firestore';
import { useAuth } from '../../AuthContext';

// Constants for localStorage keys and token expiration
const LOCAL_STORAGE_KEYS = {
  ACCESS_TOKEN: 'meta_ads_access_token',
  TOKEN_EXPIRY: 'meta_ads_token_expiry'
};

// Default token expiration in seconds (60 days)
const DEFAULT_TOKEN_EXPIRATION = 60 * 24 * 60 * 60;

interface LeadForm {
  id: string;
  name: string;
  status: string;
  created_time?: string;
  locale?: string;
}

interface LeadData {
  id: string;
  created_time: string;
  field_data: {
    name: string;
    values: string[];
  }[];
  form_id: string;
}

interface User {
  id: string;
  email: string;
  name: string;
  role?: string;
  companyId: string;
  photoURL?: string;
}

interface Campaign {
  id: string;
  name: string; // For UI display, derived from campaign field
  campaign: string; // The actual campaign name in document
  isActive: boolean; // To filter active campaigns only
}

interface Source {
  id: string;
  name: string; // For UI display, derived from source field
  channel: string; // Channel field from document
  source: string; // Source field from document
}

interface MetaPage {
  id: string;
  name: string;
  category?: string;
  category_list?: {id: string; name: string}[];
  leadForms?: LeadForm[];
  access_token?: string;
  tasks?: string[];
}

// Removed MetaAd interface to focus only on lead forms

interface MetaAdAccount {
  id: string;
  name: string;
  type?: string;
  connectedDate?: string;
  business_name?: string;
  currency?: string;
  pages?: MetaPage[];
}

const MetaAds = () => {
    const isCollapsed = useAppSelector((state) => state.sidebar.isCollapsed);
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [isAddingAccount, setIsAddingAccount] = useState(false);
    const [loadingType, setLoadingType] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [tokenExpired, setTokenExpired] = useState(false);
    const [pages, setPages] = useState<MetaPage[]>([]);
    const [selectedPage, setSelectedPage] = useState<MetaPage | null>(null);
    const [leadForms, setLeadForms] = useState<LeadForm[]>([]);
    const [selectedFormId, setSelectedFormId] = useState<string | null>(null);
    const [leads, setLeads] = useState<LeadData[]>([]);
    const [showLeadsModal, setShowLeadsModal] = useState(false);
    const [selectedLeadForms, setSelectedLeadForms] = useState<{[key: string]: boolean}>({});
    const [originalLeadForms, setOriginalLeadForms] = useState<{[key: string]: boolean}>({});
    const [savingLeadForms, setSavingLeadForms] = useState(false);
    const [leadFormsSaveSuccess, setLeadFormsSaveSuccess] = useState(false);
    const [leadFormsSaveError, setLeadFormsSaveError] = useState<string | null>(null);
    const [companyUsers, setCompanyUsers] = useState<User[]>([]);
    const [showUserAssignmentModal, setShowUserAssignmentModal] = useState(false);
    const [showEditFormModal, setShowEditFormModal] = useState(false);
    const [currentFormBeingAssigned, setCurrentFormBeingAssigned] = useState<string | null>(null);
    const [currentFormBeingEdited, setCurrentFormBeingEdited] = useState<string | null>(null);
    const [selectedUsers, setSelectedUsers] = useState<{[key: string]: boolean}>({});
    const [formAssignedUsers, setFormAssignedUsers] = useState<{[formId: string]: string[]}>({});
    const [userSearch, setUserSearch] = useState('');
    const [showDeselectionConfirm, setShowDeselectionConfirm] = useState(false);
    const [formBeingDeselected, setFormBeingDeselected] = useState<string | null>(null);
    const [editingFormDetails, setEditingFormDetails] = useState(false);
    
    // Campaign and Source state
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [sources, setSources] = useState<Source[]>([]);
    const [selectedCampaignId, setSelectedCampaignId] = useState<string>('');
    const [selectedSourceId, setSelectedSourceId] = useState<string>('');
    const [formAssignedCampaigns, setFormAssignedCampaigns] = useState<{[formId: string]: string}>({});
    const [formAssignedSources, setFormAssignedSources] = useState<{[formId: string]: string}>({});
    const [validationErrors, setValidationErrors] = useState<{campaign?: string; source?: string}>({});
    const [toastMessage, setToastMessage] = useState<string>('');
    const [isErrorToast, setIsErrorToast] = useState<boolean>(false);
    const [showToast, setShowToast] = useState<boolean>(false);
    
    // Function to select or deselect all users
    const handleSelectAllUsers = (select: boolean) => {
        const newSelectedUsers: {[key: string]: boolean} = {};
        
        // If we're filtering users, only select/deselect the filtered ones
        const usersToUpdate = userSearch.trim() ? 
            companyUsers.filter(user => 
                (user.name?.toLowerCase() || '').includes(userSearch.toLowerCase()) ||
                (user.email?.toLowerCase() || '').includes(userSearch.toLowerCase()) ||
                (user.role?.toLowerCase() || '').includes(userSearch.toLowerCase())
            ) : 
            companyUsers;
        
        // Copy existing selections
        Object.keys(selectedUsers).forEach(id => {
            newSelectedUsers[id] = selectedUsers[id];
        });
        
        // Update with new selections
        usersToUpdate.forEach(user => {
            newSelectedUsers[user.id] = select;
        });
        
        setSelectedUsers(newSelectedUsers);
    };
    const { userData } = useAuth();
    const [connectedAccounts, setConnectedAccounts] = useState<MetaAdAccount[]>([
        // Sample data - in a real app, this would come from your backend
        {
            id: 'act_123456789',
            name: 'ScholarCRM Primary',
            type: 'Business',
            connectedDate: '2025-02-15',
            pages: [
                {
                    id: 'page_1001',
                    name: 'ScholarCRM Education',
                    category: 'Education',
                    leadForms: [
                        {
                            id: 'form_2001',
                            name: 'Student Application Form',
                            status: 'active',
                            created_time: '2025-01-15'
                        },
                        {
                            id: 'form_2002',
                            name: 'Campus Tour Request',
                            status: 'active',
                            created_time: '2025-01-28'
                        }
                    ]
                },
                {
                    id: 'page_1002',
                    name: 'ScholarCRM Training Center',
                    category: 'Education',
                    leadForms: [
                        {
                            id: 'form_3001',
                            name: 'Course Inquiry Form',
                            status: 'active',
                            created_time: '2025-02-05'
                        }
                    ]
                }
            ]
        }
    ]);

    const handleConnectAccount = () => {
        setIsAddingAccount(true);
    };

    const handleAddNewAccount = () => {
        setIsLoading(true);
        // Simulating an API call to connect a new account
        setTimeout(() => {
            const newAccount: MetaAdAccount = {
                id: `act_${Math.floor(Math.random() * 1000000000)}`,
                name: `ScholarCRM ${connectedAccounts.length + 1}`,
                type: 'Business',
                connectedDate: new Date().toISOString().split('T')[0],
                pages: [
                    {
                        id: `page_${Math.floor(Math.random() * 1000000)}`,
                        name: `${connectedAccounts.length + 1} Business Page`,
                        category: 'Business',
                        leadForms: [
                            {
                                id: `form_${Math.floor(Math.random() * 1000000)}`,
                                name: 'Default Lead Form',
                                status: 'active',
                                created_time: new Date().toISOString().split('T')[0]
                            }
                        ]
                    }
                ]
            };
            setConnectedAccounts([...connectedAccounts, newAccount]);
            setIsLoading(false);
            setIsAddingAccount(false);
        }, 1500);
    };

    const handleDisconnect = (accountId: string) => {
        setIsLoading(true);
        // Simulating an API call to disconnect a specific account
        setTimeout(() => {
            setConnectedAccounts(connectedAccounts.filter(account => account.id !== accountId));
            setIsLoading(false);
        }, 1000);
    };

    const handleGoBack = () => {
        navigate('/integrations');
    };
    
    const handleCancelAddAccount = () => {
        setIsAddingAccount(false);
    };
    
    // Save token to localStorage with expiration and to Firestore
    const saveTokenToStorage = async (token: string) => {
        // Check if this account can be used with this company
        if (userData?.companyId) {
            try {
                const isAllowed = await checkCompanyFacebookAccount(token);
                if (!isAllowed) {
                    return; // Exit if verification failed
                }
                
                const now = new Date();
                const expiryTime = now.getTime() + (DEFAULT_TOKEN_EXPIRATION * 1000);
                
                localStorage.setItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN, token);
                localStorage.setItem(LOCAL_STORAGE_KEYS.TOKEN_EXPIRY, expiryTime.toString());
                
                // Get user ID from Meta API
                const userResponse = await fetch(
                    `https://graph.facebook.com/v22.0/me?access_token=${token}`
                );
                
                if (!userResponse.ok) {
                    throw new Error('Failed to get user ID from Facebook');
                }
                
                const fbUserData = await userResponse.json();
                const accountId = fbUserData.id;
                
                // Save to Firestore
                await setDoc(doc(db, 'facebookAccounts', accountId), {
                    accountId,
                    accountName: fbUserData.name || 'Meta Ads Account',
                    accountAccessToken: token,
                    accountExpiration: serverTimestamp(),
                    companyId: userData.companyId
                });
                
                console.log('Facebook account saved to Firestore');
            } catch (error) {
                console.error('Error saving token to Firestore:', error);
                setToastMessage('Failed to save Facebook account information.');
                setIsErrorToast(true);
                setShowToast(true);
            }
        }
    };
    
    // Helper function to check if a Facebook account can be used with this company
    const checkCompanyFacebookAccount = async (token: string): Promise<boolean> => {
        try {
            // Get user ID from Meta API
            const userResponse = await fetch(
                `https://graph.facebook.com/v22.0/me?access_token=${token}`
            );
            
            if (!userResponse.ok) {
                throw new Error('Failed to get user ID from Facebook');
            }
            
            const fbUserData = await userResponse.json();
            const accountId = fbUserData.id;
            
            // Check if there's already an account for this company
            if (!userData?.companyId) {
                throw new Error('No company ID found for current user');
            }
            
            const existingAccountsQuery = query(
                collection(db, 'facebookAccounts'),
                where('companyId', '==', userData.companyId)
            );
            
            const existingAccountsSnapshot = await getDocs(existingAccountsQuery);
            
            // If there's already an account for this company and it's not the current user's account
            if (!existingAccountsSnapshot.empty && !existingAccountsSnapshot.docs.some(doc => doc.id === accountId)) {
                const existingAccountDoc = existingAccountsSnapshot.docs[0];
                const existingAccountData = existingAccountDoc.data();
                
                // Extract available information about the existing admin
                const existingAdminId = existingAccountDoc.id;
                const existingAdminName = existingAccountData.accountName || 'Unknown';
                
                // Show detailed error message with account information
                setToastMessage(`This company already has a Meta Ads admin (${existingAdminName}, ID: ${existingAdminId}). Only the existing admin can make changes.`);
                setIsErrorToast(true);
                setShowToast(true);
                
                // Clear the token
                clearTokenFromStorage();
                setAccessToken(null);
                
                return false;
            }
            
            return true;
        } catch (error) {
            console.error('Error in checkCompanyFacebookAccount:', error);
            throw error; // Re-throw to be handled by the calling function
        }
    };
    
    // Clear token from localStorage
    const clearTokenFromStorage = () => {
        localStorage.removeItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
        localStorage.removeItem(LOCAL_STORAGE_KEYS.TOKEN_EXPIRY);
    };
    
    // Check if token is expired
    const isTokenExpired = () => {
        const expiryTime = localStorage.getItem(LOCAL_STORAGE_KEYS.TOKEN_EXPIRY);
        if (!expiryTime) return true;
        
        const now = new Date().getTime();
        return now > parseInt(expiryTime);
    };
    
    // Connect to Facebook
    const handleConnectToFacebook = () => {
        setTokenExpired(false);
        const fbOAuthUrl = new URL('https://www.facebook.com/v22.0/dialog/oauth');
        
        // Add query parameters
        fbOAuthUrl.searchParams.append('response_type', 'token');
        fbOAuthUrl.searchParams.append('display', 'popup');
        fbOAuthUrl.searchParams.append('client_id', '1201840001461838');
        fbOAuthUrl.searchParams.append('redirect_uri', `${window.location.origin}/integrations/meta-ads`);
        fbOAuthUrl.searchParams.append('auth_type', 'rerequest');
        fbOAuthUrl.searchParams.append('scope', 'read_insights,pages_show_list,ads_management,ads_read,business_management,leads_retrieval,pages_manage_metadata,pages_manage_ads');
        
        // Redirect to Facebook OAuth dialog
        window.location.href = fbOAuthUrl.toString();
    };
    
    // Disconnect from Facebook (frontend only)
    const handleDisconnectFacebook = () => {
        // Clear token from localStorage
        clearTokenFromStorage();
        
        // Reset frontend state
        setAccessToken(null);
        setPages([]);
        setSelectedPage(null);
        setLeadForms([]);
        setSelectedLeadForms({});
        setOriginalLeadForms({});
        
        console.log('Disconnected from Facebook (frontend only)');
    };
    
    const fetchPages = async () => {
        if (!accessToken) return;
        
        try {
            setLoadingType('pages');
            setErrorMessage(null);
            
            const response = await fetch(
                `https://graph.facebook.com/v22.0/me/accounts?fields=id,name,category,category_list,tasks,access_token&access_token=${accessToken}`
            );
            
            if (!response.ok) {
                const errorData = await response.json();
                
                // Check for expired token error
                if (errorData.error?.code === 190 || errorData.error?.message?.includes('expired')) {
                    setTokenExpired(true);
                    clearTokenFromStorage();
                    setAccessToken(null);
                    throw new Error('Your Facebook session has expired. Please reconnect to continue.');
                }
                
                throw new Error(errorData.error?.message || 'Failed to fetch pages');
            }
            
            const data = await response.json();
            if (data.data && Array.isArray(data.data)) {
                setPages(data.data);
                
                // Don't save pages to Firestore immediately
                // Pages will only be saved when lead forms from them are selected
                console.log('Pages fetched successfully. They will be saved to Firestore when lead forms are selected.');
            }
        } catch (error) {
            console.error('Error fetching pages:', error);
            setErrorMessage(error instanceof Error ? error.message : 'Failed to fetch pages');
        } finally {
            setLoadingType(null);
        }
    };
    
    const fetchLeadForms = async (pageId: string, pageAccessToken: string) => {
        try {
            setLoadingType('forms');
            setErrorMessage(null);
            
            const response = await fetch(
                `https://graph.facebook.com/v22.0/${pageId}/leadgen_forms?fields=id,name,status,created_time,locale&access_token=${pageAccessToken}`
            );
            
            if (!response.ok) {
                const errorData = await response.json();
                
                // Check for expired token error
                if (errorData.error?.code === 190 || errorData.error?.message?.includes('expired')) {
                    setTokenExpired(true);
                    clearTokenFromStorage();
                    setAccessToken(null);
                    setSelectedPage(null);
                    throw new Error('Your Facebook session has expired. Please reconnect to continue.');
                }
                
                throw new Error(errorData.error?.message || 'Failed to fetch lead forms');
            }
            
            const data = await response.json();
            if (data.data && Array.isArray(data.data)) {
                setLeadForms(data.data);
                
                // Check if any lead forms are saved in Firestore
                if (userData?.companyId && accessToken) {
                    try {
                        // Get account ID
                        const userResponse = await fetch(
                            `https://graph.facebook.com/v22.0/me?access_token=${accessToken}`
                        );
                        
                        if (userResponse.ok) {
                            const fbUserData = await userResponse.json();
                            const accountId = fbUserData.id;
                            
                            // Query saved lead forms from subcollection
                            const formsCollection = collection(
                                db, 
                                'facebookAccounts', 
                                accountId, 
                                'facebookPages', 
                                pageId, 
                                'leadGenForms'
                            );
                            const formsQuery = query(formsCollection, where('companyId', '==', userData.companyId));
                            const formsSnapshot = await getDocs(formsQuery);
                            const savedForms: {[key: string]: boolean} = {};
                            const assignedUsersMap: {[formId: string]: string[]} = {};
                            const campaignsMap: {[formId: string]: string} = {};
                            const sourcesMap: {[formId: string]: string} = {};
                            
                            formsSnapshot.forEach(formDoc => {
                                savedForms[formDoc.id] = true;
                                const data = formDoc.data();
                                if (data.assignedUsers && Array.isArray(data.assignedUsers)) {
                                    assignedUsersMap[formDoc.id] = data.assignedUsers;
                                }
                                
                                if (data.campaignId) {
                                    campaignsMap[formDoc.id] = data.campaignId;
                                }
                                
                                if (data.sourceId) {
                                    sourcesMap[formDoc.id] = data.sourceId;
                                }
                            });
                            
                            setSelectedLeadForms(savedForms);
                            setOriginalLeadForms({...savedForms});
                            setFormAssignedUsers(assignedUsersMap);
                            setFormAssignedCampaigns(campaignsMap);
                            setFormAssignedSources(sourcesMap);
                        }
                    } catch (error) {
                        console.error('Error fetching saved lead forms from Firestore:', error);
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching lead forms:', error);
            setErrorMessage(error instanceof Error ? error.message : 'Failed to fetch lead forms');
        } finally {
            setLoadingType(null);
        }
    };
    
    const fetchLeads = async (formId: string, pageAccessToken: string) => {
        try {
            setLoadingType('leads');
            setErrorMessage(null);
            setSelectedFormId(formId);
            
            const response = await fetch(
                `https://graph.facebook.com/v22.0/${formId}/leads?fields=id,created_time,field_data&access_token=${pageAccessToken}`
            );
            
            if (!response.ok) {
                const errorData = await response.json();
                
                // Check for expired token error
                if (errorData.error?.code === 190 || errorData.error?.message?.includes('expired')) {
                    setTokenExpired(true);
                    clearTokenFromStorage();
                    setAccessToken(null);
                    throw new Error('Your Facebook session has expired. Please reconnect to continue.');
                }
                
                throw new Error(errorData.error?.message || 'Failed to fetch leads');
            }
            
            const data = await response.json();
            if (data.data && Array.isArray(data.data)) {
                setLeads(data.data.map((lead: any) => ({
                    ...lead,
                    form_id: formId
                })));
                setShowLeadsModal(true);
            } else {
                setLeads([]);
                setShowLeadsModal(true);
            }
        } catch (error) {
            console.error('Error fetching leads:', error);
            setErrorMessage(error instanceof Error ? error.message : 'Failed to fetch leads');
        } finally {
            setLoadingType(null);
        }
    };
    
    const handleViewLeads = (formId: string) => {
        if (selectedPage?.access_token) {
            fetchLeads(formId, selectedPage.access_token);
        }
    };
    
    const closeLeadsModal = () => {
        setShowLeadsModal(false);
        setLeads([]);
        setSelectedFormId(null);
    };
    
    // Function to export leads to CSV
    const exportLeads = () => {
        if (leads.length === 0) return;
        
        try {
            // Get the form name for the filename
            const formName = leadForms.find(form => form.id === selectedFormId)?.name || 'lead-form';
            const filename = `${formName.replace(/\s+/g, '-').toLowerCase()}-leads-${new Date().toISOString().split('T')[0]}.csv`;
            
            // Create headers from all possible field names
            const allFieldNames = new Set<string>();
            leads.forEach(lead => {
                lead.field_data.forEach(field => {
                    allFieldNames.add(field.name);
                });
            });
            
            const headers = ['Lead ID', 'Created Time', ...Array.from(allFieldNames)];
            
            // Create rows for each lead
            const rows = leads.map(lead => {
                const row: {[key: string]: string} = {
                    'Lead ID': lead.id,
                    'Created Time': new Date(lead.created_time).toLocaleString()
                };
                
                // Add field data
                lead.field_data.forEach(field => {
                    row[field.name] = field.values ? field.values.join(', ') : '';
                });
                
                return row;
            });
            
            // Convert to CSV
            let csv = headers.join(',') + '\n';
            
            rows.forEach(row => {
                const values = headers.map(header => {
                    const value = row[header] || '';
                    // Escape quotes and wrap with quotes if contains comma
                    return value.includes(',') ? `"${value.replace(/"/g, '""')}"` : value;
                });
                csv += values.join(',') + '\n';
            });
            
            // Create and download the file
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
        } catch (error) {
            console.error('Error exporting leads:', error);
            setErrorMessage('Failed to export leads. Please try again.');
        }
    };
    
    // Removed fetchAds function to focus only on lead forms
    
    const handleSelectPage = (page: MetaPage) => {
        setSelectedPage(page);
        if (page.id && page.access_token) {
            fetchLeadForms(page.id, page.access_token);
        }
    };
    
    const filteredAccounts = searchQuery 
        ? connectedAccounts.filter(account => 
            account.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
            account.id.toLowerCase().includes(searchQuery.toLowerCase()))
        : connectedAccounts;

    // Load token from localStorage or extract from URL on component mount
    useEffect(() => {
        // First check URL for fresh token (after OAuth redirect) - this happens on sign-in/re-sign-in
        const hashParams = window.location.hash;
        if (hashParams && hashParams.includes('access_token')) {
            // Remove the '#' character and parse the parameters
            const params = new URLSearchParams(hashParams.substring(1));
            const token = params.get('access_token');
            if (token) {
                setAccessToken(token);
                // Save token to both localStorage and Firestore ONLY on explicit sign-in/re-sign-in
                saveTokenToStorage(token);
                // Clean up the URL without causing a page reload
                window.history.replaceState(null, '', window.location.pathname);
                return;
            }
        }
        
        // If no token in URL, try to load from localStorage
        const storedToken = localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
        if (storedToken) {
            // Check if token is expired
            if (isTokenExpired()) {
                setTokenExpired(true);
                clearTokenFromStorage();
            } else {
                setAccessToken(storedToken);
            }
        }
    }, []);
    
    // Fetch pages when access token is available
    useEffect(() => {
        if (accessToken) {
            fetchPages();
        }
    }, [accessToken]);
    
    // Fetch all company users, campaigns, and sources when component mounts
    useEffect(() => {
        if (userData?.companyId) {
            fetchCompanyUsers();
            fetchCampaigns();
            fetchSources();
        }
    }, [userData]);

    // Fetch all company users with the same companyId
    const fetchCompanyUsers = async () => {
        if (!userData?.companyId) return;
        
        try {
            const usersQuery = query(
                collection(db, 'users'),
                where('companyId', '==', userData.companyId)
            );
            
            const usersSnapshot = await getDocs(usersQuery);
            const users: User[] = [];
            
            usersSnapshot.forEach(doc => {
                users.push({
                    id: doc.id,
                    ...doc.data(),
                } as User);
            });
            
            setCompanyUsers(users);
        } catch (error) {
            console.error('Error fetching company users:', error);
        }
    };
    
    // Fetch all campaigns for the company
    const fetchCampaigns = async () => {
        if (!userData?.companyId) return;
        
        try {
            const campaignsQuery = query(
                collection(db, 'companies', userData.companyId, 'leadCampaigns')
            );
            
            const campaignsSnapshot = await getDocs(campaignsQuery);
            const campaignList: Campaign[] = [];
            
            campaignsSnapshot.forEach(doc => {
                const data = doc.data();
                
                // Only include active campaigns
                if (data.isActive === true) {
                    campaignList.push({
                        id: doc.id,
                        name: data.campaign || 'Unnamed Campaign', // Use campaign field as the display name
                        ...data
                    } as Campaign);
                }
            });
            
            setCampaigns(campaignList);
        } catch (error) {
            console.error('Error fetching campaigns:', error);
        }
    };
    
    // Fetch all sources for the company
    const fetchSources = async () => {
        if (!userData?.companyId) return;
        
        try {
            const sourcesQuery = query(
                collection(db, 'companies', userData.companyId, 'leadSources')
            );
            
            const sourcesSnapshot = await getDocs(sourcesQuery);
            const sourceList: Source[] = [];
            
            sourcesSnapshot.forEach(doc => {
                const data = doc.data();
                sourceList.push({
                    id: doc.id,
                    name: data.source || 'Unnamed Source', // Use source field as the display name
                    ...data
                } as Source);
            });
            
            setSources(sourceList);
        } catch (error) {
            console.error('Error fetching sources:', error);
        }
    };
    
    // Handle user assignment confirmation - now saves directly to database
    const handleConfirmUserAssignment = async () => {
        if (!currentFormBeingAssigned || !selectedPage || !userData?.companyId || !accessToken) return;
        
        // Validate required fields
        const errors: {campaign?: string; source?: string} = {};
        
        if (!selectedCampaignId) {
            errors.campaign = 'Campaign is required';
        }
        
        if (!selectedSourceId) {
            errors.source = 'Source is required';
        }
        
        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return; // Stop the form submission if validation fails
        }
        
        // Set loading state
        setLoadingType('saving');
        setSavingLeadForms(true);
        setLeadFormsSaveSuccess(false);
        setLeadFormsSaveError(null);
        
        try {
            // Get selected user IDs
            const userIds = Object.keys(selectedUsers).filter(id => selectedUsers[id]);
            
            // Update formAssignedUsers state
            setFormAssignedUsers(prev => ({
                ...prev,
                [currentFormBeingAssigned]: userIds
            }));
            
            // Update formAssignedCampaigns and formAssignedSources state
            setFormAssignedCampaigns(prev => ({
                ...prev,
                [currentFormBeingAssigned]: selectedCampaignId
            }));
            
            setFormAssignedSources(prev => ({
                ...prev,
                [currentFormBeingAssigned]: selectedSourceId
            }));
            
            // Set the form as selected
            setSelectedLeadForms(prev => ({
                ...prev,
                [currentFormBeingAssigned]: true
            }));
            
            // Get the Facebook account ID from the Meta API
            const userResponse = await fetch(
                `https://graph.facebook.com/v22.0/me?access_token=${accessToken}`
            );
            
            if (!userResponse.ok) {
                throw new Error('Failed to get account ID from Facebook');
            }
            
            const fbUserData = await userResponse.json();
            const accountId = fbUserData.id;
            const pageId = selectedPage.id;
            const pageAccessToken = selectedPage.access_token;
            
            // Find the form data for the current form
            const form = leadForms.find(f => f.id === currentFormBeingAssigned);
            
            if (form) {
                // First save the page data to Firestore
                const pageRef = doc(db, 'facebookAccounts', accountId, 'facebookPages', pageId);
                
                await setDoc(pageRef, {
                    pageId: selectedPage.id,
                    pageName: selectedPage.name,
                    pageAccessToken: selectedPage.access_token,
                    companyId: userData.companyId,
                    accountId: accountId,
                    updatedAt: serverTimestamp()
                }, { merge: true });
                
                // Then create a reference to the form document
                const formRef = doc(db, 'facebookAccounts', accountId, 'facebookPages', pageId, 'leadGenForms', form.id);
                
                // Save the form with assigned users to Firestore
                await setDoc(formRef, {
                    formId: form.id,
                    formName: form.name,
                    formStatus: form.status,
                    pageId: selectedPage.id,
                    pageName: selectedPage.name,
                    pageAccessToken: selectedPage.access_token,
                    companyId: userData.companyId,
                    assignedUsers: userIds,
                    campaignId: selectedCampaignId,
                    sourceId: selectedSourceId,
                    accountId: accountId,
                    createdAt: form.created_time ? new Date(form.created_time) : serverTimestamp(),
                    updatedAt: serverTimestamp(),
                    lastLeadUpdatedTime: serverTimestamp()
                }, { merge: true });
                
                // Subscribe to leadgen webhooks for this page
                if (pageAccessToken) {
                    try {
                        const subscribeResponse = await fetch(
                            `https://graph.facebook.com/${pageId}/subscribed_apps?subscribed_fields=leadgen&access_token=${pageAccessToken}`,
                            {
                                method: 'POST',
                            }
                        );
                        
                        const subscribeData = await subscribeResponse.json();
                        
                        if (!subscribeResponse.ok || !subscribeData.success) {
                            console.error('Failed to subscribe to leadgen webhooks:', subscribeData);
                            // Continue with success message even if webhook subscription fails
                            // This way the form is still saved even if webhook subscription has issues
                        } else {
                            console.log('Successfully subscribed to leadgen webhooks for page:', pageId);
                        }
                    } catch (webhookError) {
                        console.error('Error subscribing to leadgen webhooks:', webhookError);
                        // Continue with success message even if webhook subscription fails
                    }
                }
                
                // Show success message
                setLeadFormsSaveSuccess(true);
                
                // Clear success message after 3 seconds
                setTimeout(() => {
                    setLeadFormsSaveSuccess(false);
                }, 3000);
            }
        } catch (error) {
            console.error('Error saving form with assigned users:', error);
            setLeadFormsSaveError(error instanceof Error ? error.message : 'Unknown error');
        } finally {
            setLoadingType(null);
            setSavingLeadForms(false);
            
            // Close the modal
            setShowUserAssignmentModal(false);
            setCurrentFormBeingAssigned(null);
            setSelectedUsers({});
            setSelectedCampaignId('');
            setSelectedSourceId('');
            setValidationErrors({});
            setUserSearch('');
        }
    };
    
    // Handle user assignment cancellation
    const handleCancelUserAssignment = () => {
        setShowUserAssignmentModal(false);
        setCurrentFormBeingAssigned(null);
        setSelectedUsers({});
        setSelectedCampaignId('');
        setSelectedSourceId('');
        setValidationErrors({});
        setUserSearch('');
    };
    
    // Handle opening the edit form modal
    const handleEditForm = (formId: string) => {
        // Find the form's existing campaign and source
        const campaignId = formAssignedCampaigns[formId] || '';
        const sourceId = formAssignedSources[formId] || '';
        
        // Get currently assigned users for this form
        const assignedUsers = formAssignedUsers[formId] || [];
        
        // Create a map of user IDs to selection state
        const initialSelectedUsers: {[key: string]: boolean} = {};
        assignedUsers.forEach(userId => {
            initialSelectedUsers[userId] = true;
        });
        
        // Set selected values
        setSelectedCampaignId(campaignId);
        setSelectedSourceId(sourceId);
        setSelectedUsers(initialSelectedUsers);
        
        // Set current form being edited
        setCurrentFormBeingEdited(formId);
        
        // Open the modal
        setShowEditFormModal(true);
        setEditingFormDetails(true);
    };
    
    // Handle edit form cancellation
    const handleCancelEditForm = () => {
        setShowEditFormModal(false);
        setCurrentFormBeingEdited(null);
        setSelectedCampaignId('');
        setSelectedSourceId('');
        setValidationErrors({});
        setEditingFormDetails(false);
    };
    
    // Handle saving edited form details
    const handleSaveFormDetails = async () => {
        if (!currentFormBeingEdited || !selectedPage || !userData?.companyId || !accessToken) return;
        
        // Validate required fields
        const errors: {campaign?: string; source?: string} = {};
        
        if (!selectedCampaignId) {
            errors.campaign = 'Campaign is required';
        }
        
        if (!selectedSourceId) {
            errors.source = 'Source is required';
        }
        
        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return; // Stop the form submission if validation fails
        }
        
        // Set loading state
        setLoadingType('saving');
        setLeadFormsSaveSuccess(false);
        setLeadFormsSaveError(null);
        
        try {
            // Get selected user IDs
            const selectedUserIds = Object.keys(selectedUsers).filter(userId => selectedUsers[userId]);
            
            // Update local state
            setFormAssignedCampaigns(prev => ({
                ...prev,
                [currentFormBeingEdited]: selectedCampaignId
            }));
            
            setFormAssignedSources(prev => ({
                ...prev,
                [currentFormBeingEdited]: selectedSourceId
            }));
            
            // Update assigned users in local state
            setFormAssignedUsers(prev => ({
                ...prev,
                [currentFormBeingEdited]: selectedUserIds
            }));
            
            // Get the Facebook account ID from the Meta API
            const userResponse = await fetch(
                `https://graph.facebook.com/v22.0/me?access_token=${accessToken}`
            );
            
            if (!userResponse.ok) {
                throw new Error('Failed to get account ID from Facebook');
            }
            
            const fbUserData = await userResponse.json();
            const accountId = fbUserData.id;
            const pageId = selectedPage.id;
            
            // Find the form data for the current form
            const form = leadForms.find(f => f.id === currentFormBeingEdited);
            
            if (form) {
                // Create a reference to the form document
                const formRef = doc(db, 'facebookAccounts', accountId, 'facebookPages', pageId, 'leadGenForms', form.id);
                
                // Save the form with updated campaign, source, and assigned users to Firestore
                await setDoc(formRef, {
                    campaignId: selectedCampaignId,
                    sourceId: selectedSourceId,
                    accountId: accountId,
                    assignedUsers: selectedUserIds,
                    updatedAt: serverTimestamp()
                }, { merge: true });
                
                // Show success message
                setLeadFormsSaveSuccess(true);
                setToastMessage('Form details and user assignments updated successfully');
                setIsErrorToast(false);
                setShowToast(true);
                
                // Clear success message after 3 seconds
                setTimeout(() => {
                    setLeadFormsSaveSuccess(false);
                    setShowToast(false);
                }, 3000);
            }
        } catch (error) {
            console.error('Error saving form details:', error);
            setLeadFormsSaveError(error instanceof Error ? error.message : 'Unknown error');
            setToastMessage('Failed to update form details');
            setIsErrorToast(true);
            setShowToast(true);
            
            // Hide error toast after 3 seconds
            setTimeout(() => setShowToast(false), 3000);
        } finally {
            setLoadingType(null);
            
            // Close the modal and reset state
            setShowEditFormModal(false);
            setCurrentFormBeingEdited(null);
            setSelectedCampaignId('');
            setSelectedSourceId('');
            setValidationErrors({});
            setEditingFormDetails(false);
            setSelectedUsers({});
            setUserSearch('');
        }
    };
    
    // Handle deselection confirmation
    const handleDeselectionConfirm = (formId: string) => {
        setFormBeingDeselected(formId);
        setShowDeselectionConfirm(true);
    };
    
    // Confirm deselection after warning
    const confirmDeselection = async () => {
        if (!formBeingDeselected || !selectedPage || !userData?.companyId || !accessToken) {
            setShowDeselectionConfirm(false);
            return;
        }
        
        try {
            setLoadingType('saving');
            
            // Get the Facebook account ID from the Meta API
            const userResponse = await fetch(
                `https://graph.facebook.com/v22.0/me?access_token=${accessToken}`
            );
            
            if (!userResponse.ok) {
                throw new Error('Failed to get account ID from Facebook');
            }
            
            const fbUserData = await userResponse.json();
            const accountId = fbUserData.id;
            const pageId = selectedPage.id;
            
            // Create a reference to the form document
            const formRef = doc(db, 'facebookAccounts', accountId, 'facebookPages', pageId, 'leadGenForms', formBeingDeselected);
            
            // Delete the form from Firestore
            await deleteDoc(formRef);
            
            // Update local state
            setSelectedLeadForms(prev => ({
                ...prev,
                [formBeingDeselected]: false
            }));
            
            // Remove assigned users, campaign, and source for this form
            setFormAssignedUsers(prev => {
                const updated = {...prev};
                delete updated[formBeingDeselected];
                return updated;
            });
            
            setFormAssignedCampaigns(prev => {
                const updated = {...prev};
                delete updated[formBeingDeselected];
                return updated;
            });
            
            setFormAssignedSources(prev => {
                const updated = {...prev};
                delete updated[formBeingDeselected];
                return updated;
            });
            
            // Check if there are any remaining lead forms for this page after the deletion
            const formCollection = collection(db, 'facebookAccounts', accountId, 'facebookPages', pageId, 'leadGenForms');
            const formSnapshot = await getDocs(query(formCollection, where('companyId', '==', userData.companyId)));
            
            // If there are no more forms, unsubscribe from the leadgen webhooks
            if (formSnapshot.empty && selectedPage?.access_token) {
                try {
                    console.log('No more lead forms for this page. Unsubscribing from leadgen webhooks...');
                    
                    // DELETE request to unsubscribe from leadgen webhooks
                    const unsubscribeResponse = await fetch(
                        `https://graph.facebook.com/${pageId}/subscribed_apps`,
                        {
                            method: 'DELETE',
                            headers: {
                                'Authorization': `Bearer ${selectedPage.access_token}`
                            }
                        }
                    );
                    
                    const unsubscribeData = await unsubscribeResponse.json();
                    
                    if (!unsubscribeResponse.ok || !unsubscribeData.success) {
                        console.error('Failed to unsubscribe from leadgen webhooks:', unsubscribeData);
                    } else {
                        console.log('Successfully unsubscribed from leadgen webhooks for page:', pageId);
                    }
                    
                    // Also delete the page document from Firestore as there are no more forms
                    const pageRef = doc(db, 'facebookAccounts', accountId, 'facebookPages', pageId);
                    await deleteDoc(pageRef);
                    console.log(`Deleting page ${pageId} from Firestore as it has no more lead forms`);
                } catch (webhookError) {
                    console.error('Error unsubscribing from leadgen webhooks or deleting page:', webhookError);
                    // Continue with success message even if webhook unsubscription fails
                }
            }
            
            // Show success message
            setLeadFormsSaveSuccess(true);
            
            // Clear success message after 3 seconds
            setTimeout(() => {
                setLeadFormsSaveSuccess(false);
            }, 3000);
            
        } catch (error) {
            console.error('Error removing lead form:', error);
            setLeadFormsSaveError(error instanceof Error ? error.message : 'Unknown error');
        } finally {
            setLoadingType(null);
            setShowDeselectionConfirm(false);
            setFormBeingDeselected(null);
        }
    };
    
    return (
        <div className="min-h-screen bg-gray-200">
            <div className="fixed top-16 left-0 h-full z-10">
                <SideNavbar />
            </div>
            <div className={`${isCollapsed ? 'ml-[70px]' : 'ml-[290px]'} mt-[109px] p-8 transition-all duration-300`}>
                <div className="bg-white p-8 rounded-3xl shadow-md">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-8">
                        <div>
                            <button 
                                onClick={handleGoBack}
                                className="text-gray-600 hover:text-gray-900 flex items-center gap-2 mb-4"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                                Back to Integrations
                            </button>
                            <h1 className="text-2xl font-bold text-gray-900">Meta Ads Integration</h1>
                            <p className="text-gray-600 mt-2">Connect your Meta Ads account to track and manage ad campaigns</p>
                        </div>
                        <div className="flex-shrink-0">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-10 h-10 text-blue-600">
                                <path d="M12 2.04c-5.5 0-10 4.49-10 10.02 0 5 3.66 9.15 8.44 9.9v-7H7.9v-2.9h2.54V9.85c0-2.51 1.49-3.89 3.78-3.89 1.09 0 2.23.19 2.23.19v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.45 2.9h-2.33v7a10 10 0 0 0 8.44-9.9c0-5.53-4.5-10.02-10-10.02z" />
                            </svg>
                        </div>
                    </div>

                    {/* Connect Button Section */}
                    <div className="mb-8 text-center">
                        <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm">
                            <h2 className="text-xl font-semibold text-gray-900 mb-4">Connect Your Meta Ads Account</h2>
                            
                            {accessToken ? (
                                <div className="mb-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                                            <span className="h-2 w-2 mr-2 bg-green-500 rounded-full"></span>
                                            Connected Successfully
                                        </span>
                                        <button
                                            onClick={handleDisconnectFacebook}
                                            className="text-sm text-red-600 hover:text-red-800 px-3 py-1 bg-red-50 rounded-md transition-colors"
                                        >
                                            Disconnect
                                        </button>
                                    </div>
                                    
                                    {tokenExpired && (
                                        <div className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-lg mb-4">
                                            <div className="flex items-start">
                                                <svg className="h-5 w-5 text-amber-400 mr-3 mt-0.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                </svg>
                                                <div>
                                                    <h3 className="text-sm font-medium">Session Expired</h3>
                                                    <p className="text-xs mt-1">Your Facebook session has expired. Please reconnect to continue.</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {errorMessage && (
                                        <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-4">
                                            {errorMessage}
                                        </div>
                                    )}
                                    {/* Pages Section */}
                                    <div className="mt-8 mb-6">
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="text-lg font-medium text-gray-900">Select a Facebook Page</h3>
                                            <span className="text-sm text-gray-500">{pages.length} pages available</span>
                                        </div>
                                        
                                        {loadingType === 'pages' ? (
                                            <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                                                <svg className="animate-spin h-6 w-6 text-blue-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                <p className="mt-3 text-sm text-gray-600">Loading your Facebook pages...</p>
                                            </div>
                                        ) : pages.length > 0 ? (
                                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                                                <div className="p-2 bg-gray-50 border-b border-gray-200">
                                                    <input
                                                        type="text"
                                                        placeholder="Search pages..."
                                                        className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                        onChange={(e) => setSearchQuery(e.target.value)}
                                                        value={searchQuery}
                                                    />
                                                </div>
                                                <div className="divide-y divide-gray-200 max-h-72 overflow-y-auto">
                                                    {pages.map(page => (
                                                        <div 
                                                            key={page.id} 
                                                            className={`p-4 transition-all cursor-pointer ${selectedPage?.id === page.id ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                                                            onClick={() => handleSelectPage(page)}
                                                        >
                                                            <div className="flex justify-between items-center">
                                                                <div 
                                                                    className="flex-1 text-left"
                                                                >
                                                                    <h4 className="font-medium text-gray-900 text-left">{page.name}</h4>
                                                                    <div className="flex flex-wrap items-center text-xs text-gray-500 mt-1 gap-2 justify-start">
                                                                        <span className="bg-gray-100 px-2 py-1 rounded">{page.category || (page.category_list && page.category_list[0]?.name) || 'Page'}</span>
                                                                        <span>ID: {page.id.substring(0, 8)}...</span>
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center space-x-3">
                                                                    {selectedPage?.id === page.id && (
                                                                        <div className="bg-blue-100 p-1 rounded-full">
                                                                            <svg className="h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                                            </svg>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>

                                            </div>
                                        ) : (
                                            <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                                                <svg className="h-12 w-12 text-gray-300 mx-auto mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16L18 6M9 6l12 12" />
                                                </svg>
                                                <p className="text-gray-700 font-medium">No Facebook pages found</p>
                                                <p className="text-sm text-gray-500 mt-1 max-w-xs mx-auto">Make sure your account has pages with the correct permissions and try again.</p>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Selected Page Details */}
                                    {selectedPage && (
                                        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 mt-8">
                                            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
                                                <div className="bg-blue-100 p-2 rounded-lg">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                    </svg>
                                                </div>
                                                <div>
                                                    <h3 className="text-xl font-semibold text-gray-900">{selectedPage.name}</h3>
                                                    <p className="text-sm text-gray-500">Page ID: {selectedPage.id}</p>
                                                </div>
                                            </div>
                                            
                                            {/* Lead Forms Section */}
                                            <div className="mt-10">
                                                <div className="flex items-center justify-between mb-4">
                                                    <h4 className="text-lg font-medium text-gray-900">Lead Generation Forms</h4>
                                                    <span className="text-sm text-gray-500">{leadForms.length} forms available</span>
                                                </div>
                                                
                                                {loadingType === 'forms' ? (
                                                    <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                                                        <svg className="animate-spin h-6 w-6 text-blue-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        <p className="mt-3 text-sm text-gray-600">Loading lead forms for {selectedPage?.name}...</p>
                                                    </div>
                                                ) : leadForms.length > 0 ? (
                                                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                                                        <div className="bg-gray-50 p-3 border-b border-gray-200">
                                                            <div className="grid grid-cols-7 text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                <div className="col-span-2 text-left">Form Name</div>
                                                                <div className="text-left">Status</div>
                                                                <div className="text-left">Campaign</div>
                                                                <div className="text-left">Source</div>
                                                                <div className="text-left">Assigned Users</div>
                                                                <div className="text-right">Actions</div>
                                                            </div>
                                                        </div>
                                                        <div className="divide-y divide-gray-200">
                                                            {leadForms.map(form => (
                                                                <div key={form.id} className="p-4 hover:bg-gray-50 transition-colors">
                                                                    <div className="grid grid-cols-7 items-center">
                                                                        <div className="col-span-2 text-left">
                                                                            <div className="text-left">
                                                                                <p className="font-medium text-gray-900 text-left">{form.name}</p>
                                                                                <p className="text-xs text-gray-500 mt-1 text-left">
                                                                                    Created: {form.created_time && new Date(form.created_time).toLocaleDateString()}
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                        <div className="text-left">
                                                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${form.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                                                                {form.status}
                                                                            </span>
                                                                        </div>
                                                                        <div className="text-left">
                                                                            {formAssignedCampaigns[form.id] ? (
                                                                                <span className="text-sm text-gray-800">
                                                                                    {campaigns.find(c => c.id === formAssignedCampaigns[form.id])?.name || 'Unknown Campaign'}
                                                                                </span>
                                                                            ) : (
                                                                                <span className="text-xs text-gray-400 italic">Not assigned</span>
                                                                            )}
                                                                        </div>
                                                                        <div className="text-left">
                                                                            {formAssignedSources[form.id] ? (
                                                                                <span className="text-sm text-gray-800">
                                                                                    {sources.find(s => s.id === formAssignedSources[form.id])?.name || 'Unknown Source'}
                                                                                </span>
                                                                            ) : (
                                                                                <span className="text-xs text-gray-400 italic">Not assigned</span>
                                                                            )}
                                                                        </div>
                                                                        <div className="text-left relative group">
                                                                            {formAssignedUsers[form.id] && formAssignedUsers[form.id].length > 0 ? (
                                                                                <div>
                                                                                    <div className="flex items-center -space-x-2">
                                                                                        {formAssignedUsers[form.id].slice(0, 3).map((userId) => {
                                                                                            const user = companyUsers.find(u => u.id === userId);
                                                                                            return user ? (
                                                                                                <div key={userId} className="h-8 w-8 rounded-full bg-white ring-2 ring-white overflow-hidden">
                                                                                                    {user.photoURL ? (
                                                                                                        <img src={user.photoURL} alt={user.name || 'User'} className="h-full w-full object-cover" />
                                                                                                    ) : (
                                                                                                        <div className="h-full w-full bg-blue-100 flex items-center justify-center">
                                                                                                            <span className="text-blue-800 font-medium text-xs">
                                                                                                                {user.name ? user.name.split(' ').map(n => n[0]).join('') : user.email ? user.email.charAt(0).toUpperCase() : 'U'}
                                                                                                            </span>
                                                                                                        </div>
                                                                                                    )}
                                                                                                </div>
                                                                                            ) : null;
                                                                                        })}
                                                                                        {formAssignedUsers[form.id].length > 3 && (
                                                                                            <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center ring-2 ring-white">
                                                                                                <span className="text-gray-700 text-xs font-medium">+{formAssignedUsers[form.id].length - 3}</span>
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                    
                                                                                    {/* Tooltip that appears on hover */}
                                                                                    <div className="hidden group-hover:block absolute z-10 mt-1 w-48 bg-white shadow-lg rounded-md p-2 text-xs border border-gray-200 left-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                                                                        <div className="py-1 font-medium text-gray-600 border-b border-gray-100 mb-1">Assigned users:</div>
                                                                                        <div className="max-h-40 overflow-y-auto">
                                                                                            {formAssignedUsers[form.id].map(userId => {
                                                                                                const user = companyUsers.find(u => u.id === userId);
                                                                                                return user ? (
                                                                                                    <div key={userId} className="flex items-center py-1">
                                                                                                        <div className="mr-2 h-5 w-5 rounded-full bg-blue-100 flex-shrink-0 overflow-hidden">
                                                                                                            {user.photoURL ? (
                                                                                                                <img src={user.photoURL} alt={user.name} className="h-full w-full object-cover" />
                                                                                                            ) : (
                                                                                                                <div className="h-full w-full flex items-center justify-center">
                                                                                                                    <span className="text-blue-800 text-xs">
                                                                                                                        {user.name ? user.name.charAt(0).toUpperCase() : user.email ? user.email.charAt(0).toUpperCase() : 'U'}
                                                                                                                    </span>
                                                                                                                </div>
                                                                                                            )}
                                                                                                        </div>
                                                                                                        <div className="truncate">
                                                                                                            <div className="font-medium">{user.name || 'Unnamed'}</div>
                                                                                                            <div className="text-gray-500 text-xs">{user.email}</div>
                                                                                                        </div>
                                                                                                    </div>
                                                                                                ) : null;
                                                                                            })}
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            ) : (
                                                                                <span className="text-xs text-gray-500 flex items-center">
                                                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                                                    </svg>
                                                                                    No users assigned
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <div className="text-right flex justify-end items-center space-x-4">
                                                                            {selectedLeadForms[form.id] && (
                                                                                <button 
                                                                                    onClick={() => handleEditForm(form.id)}
                                                                                    className="bg-indigo-50 text-indigo-600 hover:bg-indigo-100 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                                                                                >
                                                                                    Edit
                                                                                </button>
                                                                            )}
                                                                            <button 
                                                                                onClick={() => handleViewLeads(form.id)}
                                                                                className="bg-blue-50 text-blue-600 hover:bg-blue-100 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                                                                                disabled={loadingType === 'leads' && selectedFormId === form.id}
                                                                            >
                                                                                {loadingType === 'leads' && selectedFormId === form.id ? (
                                                                                    <span className="flex items-center">
                                                                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                                        </svg>
                                                                                        Loading...
                                                                                    </span>
                                                                                ) : 'View Leads'}
                                                                            </button>
                                                                            <label 
                                                                                className="flex items-center cursor-pointer" 
                                                                                onClick={(e) => e.stopPropagation()}
                                                                            >
                                                                                <input
                                                                                    type="checkbox"
                                                                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                                                    checked={!!selectedLeadForms[form.id]}
                                                                                    onChange={(e) => {
                                                                                        // Stop the event from bubbling up
                                                                                        e.stopPropagation();
                                                                                        
                                                                                        if (!selectedLeadForms[form.id]) {
                                                                                            // Open user assignment modal when checking the box
                                                                                            setCurrentFormBeingAssigned(form.id);
                                                                                            
                                                                                            // Initialize with current assigned users if any
                                                                                            const currentAssignedUsers = formAssignedUsers[form.id] || [];
                                                                                            const initialSelectedUsers: {[key: string]: boolean} = {};
                                                                                            currentAssignedUsers.forEach(userId => {
                                                                                                initialSelectedUsers[userId] = true;
                                                                                            });
                                                                                            
                                                                                            // Initialize with current campaign and source if any
                                                                                            setSelectedCampaignId(formAssignedCampaigns[form.id] || '');
                                                                                            setSelectedSourceId(formAssignedSources[form.id] || '');
                                                                                            
                                                                                            // Clear any validation errors
                                                                                            setValidationErrors({});
                                                                                            
                                                                                            setSelectedUsers(initialSelectedUsers);
                                                                                            setShowUserAssignmentModal(true);
                                                                                        } else {
                                                                                            // Show confirmation popup before deselecting
                                                                                            handleDeselectionConfirm(form.id);
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            </label>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                        <div className="p-4 bg-gray-50 border-t border-gray-200">
                                                            <div className="text-xs text-gray-500">
                                                                Select or deselect checkboxes to assign users to lead forms
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                                                        <svg className="h-12 w-12 text-gray-400 mx-auto mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                        </svg>
                                                        <p className="text-gray-700 font-medium">No lead forms found</p>
                                                        <p className="text-sm text-gray-500 mt-1 max-w-xs mx-auto">There are no lead forms associated with this page. Create a lead form in your Facebook Page to get started.</p>
                                                    </div>
                                                )}
                                            </div>
                                            
                                            {/* Removed Ads section to focus only on lead forms */}
                                            
                                            {/* Edit Form Modal */}
                                            {showEditFormModal && currentFormBeingEdited && (
                                                <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex justify-center items-center z-50">
                                                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
                                                        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                                                            <div>
                                                                <h3 className="text-base font-medium text-gray-900">Edit Lead Form Details</h3>
                                                                <p className="mt-0.5 text-xs text-gray-500">
                                                                    {leadForms.find(f => f.id === currentFormBeingEdited)?.name || 'Unknown Form'}
                                                                </p>
                                                            </div>
                                                            <button 
                                                                onClick={handleCancelEditForm}
                                                                className="text-gray-400 hover:text-gray-500 focus:outline-none"
                                                            >
                                                                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                        
                                                        {/* Campaign and Source Selection */}
                                                        <div className="p-3 border-b border-gray-200 bg-gray-50">
                                                            <div className="mb-3 grid grid-cols-1 gap-3">
                                                                <div>
                                                                    <label htmlFor="campaign-select" className="block text-xs font-medium text-gray-700 mb-1">
                                                                        Campaign <span className="text-red-500">*</span>
                                                                    </label>
                                                                    <div>
                                                                        <select
                                                                            id="campaign-select"
                                                                            value={selectedCampaignId}
                                                                            onChange={(e) => {
                                                                                setSelectedCampaignId(e.target.value);
                                                                                setValidationErrors(prev => ({ ...prev, campaign: undefined }));
                                                                            }}
                                                                            className={`block w-full py-1.5 text-xs border ${validationErrors.campaign ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'} rounded-md`}
                                                                        >
                                                                            <option value="">Select a campaign</option>
                                                                            {campaigns.map(campaign => (
                                                                                <option key={campaign.id} value={campaign.id}>
                                                                                    {campaign.name}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                        {validationErrors.campaign && (
                                                                            <p className="mt-1 text-xs text-red-500">{validationErrors.campaign}</p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                
                                                                <div>
                                                                    <label htmlFor="source-select" className="block text-xs font-medium text-gray-700 mb-1">
                                                                        Source <span className="text-red-500">*</span>
                                                                    </label>
                                                                    <div>
                                                                        <select
                                                                            id="source-select"
                                                                            value={selectedSourceId}
                                                                            onChange={(e) => {
                                                                                setSelectedSourceId(e.target.value);
                                                                                setValidationErrors(prev => ({ ...prev, source: undefined }));
                                                                            }}
                                                                            className={`block w-full py-1.5 text-xs border ${validationErrors.source ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'} rounded-md`}
                                                                        >
                                                                            <option value="">Select a source</option>
                                                                            {sources.map(source => (
                                                                                <option key={source.id} value={source.id}>
                                                                                    {source.name}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                        {validationErrors.source && (
                                                                            <p className="mt-1 text-xs text-red-500">{validationErrors.source}</p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            
                                                            {/* User Selection */}
                                                            <div className="mt-3 border-t border-gray-200 pt-3">
                                                                <label className="block text-xs font-medium text-gray-700 mb-2">Select Users</label>
                                                                <div className="flex items-center space-x-2">
                                                                    <div className="relative flex-1">
                                                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                                            <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                                            </svg>
                                                                        </div>
                                                                        <input
                                                                            type="text"
                                                                            placeholder="Search users..."
                                                                            className="block w-full pl-10 pr-3 py-1.5 text-xs border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                                                            value={userSearch}
                                                                            onChange={(e) => setUserSearch(e.target.value)}
                                                                        />
                                                                    </div>
                                                                    <div className="flex items-center">
                                                                        <button 
                                                                            onClick={() => handleSelectAllUsers(true)}
                                                                            className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 bg-blue-50 rounded-l-md border border-blue-100">
                                                                            Select All
                                                                        </button>
                                                                        <button 
                                                                            onClick={() => handleSelectAllUsers(false)}
                                                                            className="text-xs text-red-600 hover:text-red-800 px-2 py-1 bg-red-50 rounded-r-md border border-red-100 border-l-0">
                                                                            Clear
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        
                                                        <div className="max-h-72 overflow-y-auto">
                                                            {companyUsers.length > 0 ? (
                                                                <div>
                                                                    {companyUsers
                                                                        .filter(user => 
                                                                            userSearch === '' ||
                                                                            (user.name?.toLowerCase() || '').includes(userSearch.toLowerCase()) ||
                                                                            (user.email?.toLowerCase() || '').includes(userSearch.toLowerCase()) ||
                                                                            (user.role?.toLowerCase() || '').includes(userSearch.toLowerCase())
                                                                        )
                                                                        .map(user => (
                                                                            <div key={user.id} className="flex items-center px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0">
                                                                                <div className="flex-shrink-0 mr-2">
                                                                                    {user.photoURL ? (
                                                                                        <img 
                                                                                            src={user.photoURL} 
                                                                                            alt={user.name} 
                                                                                            className="h-7 w-7 rounded-full"
                                                                                        />
                                                                                    ) : (
                                                                                        <div className="h-7 w-7 rounded-full bg-blue-100 flex items-center justify-center">
                                                                                            <span className="text-blue-800 font-medium text-xs">
                                                                                                {user.name ? user.name.split(' ').map(n => n[0]).join('') : user.email ? user.email.charAt(0).toUpperCase() : 'U'}
                                                                                            </span>
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                                <div className="flex-1 min-w-0 leading-tight">
                                                                                    <div className="font-medium text-xs text-gray-900 truncate">{user.name || 'Unnamed User'}</div>
                                                                                    <div className="text-xs text-gray-500 truncate">{user.email}</div>
                                                                                </div>
                                                                                <div className="flex items-center space-x-2">
                                                                                    {user.role && <span className="inline-block px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">{user.role}</span>}
                                                                                    <div className="flex-shrink-0">
                                                                                        <input 
                                                                                            type="checkbox" 
                                                                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                                                            checked={!!selectedUsers[user.id]}
                                                                                            onChange={() => {
                                                                                                setSelectedUsers(prev => ({
                                                                                                    ...prev,
                                                                                                    [user.id]: !prev[user.id]
                                                                                                }));
                                                                                            }}
                                                                                        />
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        ))
                                                                    }
                                                                    {userSearch && companyUsers.filter(user => 
                                                                        (user.name?.toLowerCase() || '').includes(userSearch.toLowerCase()) ||
                                                                        (user.email?.toLowerCase() || '').includes(userSearch.toLowerCase()) ||
                                                                        (user.role?.toLowerCase() || '').includes(userSearch.toLowerCase())
                                                                    ).length === 0 && (
                                                                        <div className="text-center py-4 text-xs text-gray-500">
                                                                            No users found matching "{userSearch}"
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <div className="text-center py-6">
                                                                    <svg className="h-8 w-8 text-gray-400 mx-auto mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                                                    </svg>
                                                                    <p className="text-xs text-gray-500">No company users found</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                        
                                                        <div className="p-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
                                                            <div className="text-xs text-gray-500">
                                                                {Object.keys(selectedUsers).filter(id => selectedUsers[id]).length} users selected
                                                            </div>
                                                            <div className="flex space-x-2">
                                                                <button
                                                                    onClick={handleCancelEditForm}
                                                                    className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-50 focus:outline-none"
                                                                >
                                                                    Cancel
                                                                </button>
                                                                <button
                                                                    onClick={handleSaveFormDetails}
                                                                    className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                                                    disabled={loadingType === 'saving'}
                                                                >
                                                                    {loadingType === 'saving' ? (
                                                                        <span className="flex items-center">
                                                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                            </svg>
                                                                            Saving...
                                                                        </span>
                                                                    ) : 'Save Changes'}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            {/* End Edit Form Modal */}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center mb-8">
                                    <div className="bg-blue-50 p-4 rounded-lg mb-6">
                                        <p className="text-gray-800 mb-3">Connect ScholarCRM with your Meta Ads account to:</p>
                                        <ul className="text-gray-700 text-left list-disc pl-5 space-y-2">
                                            <li>Import leads directly from Facebook Lead Forms</li>
                                            <li>Automatically sync new leads as they come in</li>
                                            <li>Track performance of your lead generation campaigns</li>
                                        </ul>
                                    </div>
                                </div>
                            )}
                            
                            <button
                                onClick={handleConnectToFacebook}
                                className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all flex items-center gap-2 mx-auto"
                                aria-label="Connect to Facebook"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                                    <path d="M12 2.04c-5.5 0-10 4.49-10 10.02 0 5 3.66 9.15 8.44 9.9v-7H7.9v-2.9h2.54V9.85c0-2.51 1.49-3.89 3.78-3.89 1.09 0 2.23.19 2.23.19v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.45 2.9h-2.33v7a10 10 0 0 0 8.44-9.9c0-5.53-4.5-10.02-10-10.02z" />
                                </svg>
                                {accessToken ? 'Reconnect to Meta Ads Account' : 'Connect to Meta Ads Account'}
                            </button>
                        </div>
                    </div>

                    {/* Help & Support Section */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl shadow-sm mt-8">
                        <div className="flex items-start">
                            <div className="bg-white p-2 rounded-lg shadow-sm mr-4">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-2">Need Help?</h3>
                                <p className="text-gray-700 text-sm mb-4">For assistance with setting up your Meta Ads integration or connecting lead forms, please contact our support team.</p>
                                <a 
                                    href="mailto:support@scholarcred.com" 
                                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    Contact Support
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Leads Modal */}
            {showLeadsModal && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
                        {/* Modal Header */}
                        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                            <div>
                                <h3 className="text-xl font-semibold text-gray-900">
                                    {leadForms.find(form => form.id === selectedFormId)?.name || 'Lead Form'} Leads
                                </h3>
                                <p className="text-sm text-gray-500 mt-1">
                                    Showing {leads.length} leads from this form
                                </p>
                            </div>
                            <button 
                                onClick={closeLeadsModal}
                                className="text-gray-400 hover:text-gray-500 focus:outline-none">
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        
                        {/* Modal Content */}
                        <div className="flex-1 overflow-y-auto p-6">
                            {leads.length > 0 ? (
                                <div className="divide-y divide-gray-200">
                                    {leads.map(lead => {
                                        // Format the lead data for display
                                        const formattedDate = new Date(lead.created_time).toLocaleString();
                                        
                                        return (
                                            <div key={lead.id} className="py-4 first:pt-0 last:pb-0">
                                                <div className="flex justify-between items-start mb-2">
                                                    <div className="text-sm font-medium text-gray-900">Lead ID: {lead.id}</div>
                                                    <div className="text-xs text-gray-500">{formattedDate}</div>
                                                </div>
                                                
                                                <div className="mt-3 bg-gray-50 rounded-lg p-4">
                                                    <h4 className="text-xs uppercase font-medium text-gray-500 mb-2">Lead Information</h4>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                        {lead.field_data.map((field, index) => (
                                                            <div key={index} className="bg-white p-3 rounded border border-gray-200">
                                                                <div className="text-xs text-gray-500 mb-1">{field.name}</div>
                                                                <div className="text-sm font-medium">
                                                                    {field.values && field.values.length > 0 
                                                                        ? field.values.join(', ') 
                                                                        : <span className="text-gray-400 italic">No value</span>}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                                
                                                {/* Removed Add to CRM button as requested */}
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="text-center py-12 px-4">
                                    <svg className="h-16 w-16 text-gray-300 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 0112 0 4 4 0 018 0v1H3v-1z" />
                                    </svg>
                                    <h3 className="text-lg font-medium text-gray-900 mb-1">No leads found</h3>
                                    <p className="text-gray-500 max-w-md mx-auto mb-6">
                                        There are no leads associated with this form yet. Leads will appear here when people submit information through this form on Facebook.
                                    </p>
                                </div>
                            )}
                        </div>
                        
                        {/* Modal Footer */}
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
                            <div className="text-xs text-gray-500">
                                Last updated: {new Date().toLocaleString()}
                            </div>
                            <div className="flex space-x-3">
                                <button 
                                    onClick={closeLeadsModal}
                                    className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none">
                                    Close
                                </button>
                                <button 
                                    onClick={exportLeads}
                                    className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none">
                                    Export Leads
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Deselection Confirmation Modal */}
            {showDeselectionConfirm && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex justify-center items-center z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
                        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                            <div className="flex items-center space-x-2">
                                <svg className="h-6 w-6 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                                <h3 className="text-base font-medium text-gray-900">Confirm Lead Form Removal</h3>
                            </div>
                            <button 
                                onClick={() => setShowDeselectionConfirm(false)}
                                className="text-gray-400 hover:text-gray-500 focus:outline-none"
                            >
                                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        
                        <div className="p-4">
                            <div className="text-sm text-gray-600">
                                <p className="mb-3"><strong>Warning:</strong> No more leads will be captured from this lead form once it's removed.</p>
                                <p>Are you sure you want to continue?</p>
                            </div>
                        </div>
                        
                        <div className="p-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end space-x-3">
                            <button
                                onClick={() => setShowDeselectionConfirm(false)}
                                className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-50 focus:outline-none"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDeselection}
                                className="px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                                {loadingType === 'saving' ? (
                                    <span className="flex items-center">
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Removing...
                                    </span>
                                ) : 'Continue'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* User Assignment Modal */}
            {showUserAssignmentModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex justify-center items-center z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
                        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                            <div>
                                <h3 className="text-base font-medium text-gray-900">Assign Users to Lead Form</h3>
                                <p className="mt-0.5 text-xs text-gray-500">
                                    Select users who will handle leads from this form
                                </p>
                            </div>
                            <button 
                                onClick={handleCancelUserAssignment}
                                className="text-gray-400 hover:text-gray-500 focus:outline-none"
                            >
                                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        
                        {/* Search and Select All */}
                        <div className="p-3 border-b border-gray-200 bg-gray-50">
                            {/* Campaign and Source Selection */}
                            <div className="mb-3 grid grid-cols-1 gap-3">
                                <div>
                                    <label htmlFor="campaign-select" className="block text-xs font-medium text-gray-700 mb-1">
                                        Campaign <span className="text-red-500">*</span>
                                    </label>
                                    <div>
                                        <select
                                            id="campaign-select"
                                            value={selectedCampaignId}
                                            onChange={(e) => {
                                                setSelectedCampaignId(e.target.value);
                                                setValidationErrors(prev => ({ ...prev, campaign: undefined }));
                                            }}
                                            className={`block w-full py-1.5 text-xs border ${validationErrors.campaign ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'} rounded-md`}
                                        >
                                            <option value="">Select a campaign</option>
                                            {campaigns.map(campaign => (
                                                <option key={campaign.id} value={campaign.id}>
                                                    {campaign.name}
                                                </option>
                                            ))}
                                        </select>
                                        {validationErrors.campaign && (
                                            <p className="mt-1 text-xs text-red-500">{validationErrors.campaign}</p>
                                        )}
                                    </div>
                                </div>
                                
                                <div>
                                    <label htmlFor="source-select" className="block text-xs font-medium text-gray-700 mb-1">
                                        Source <span className="text-red-500">*</span>
                                    </label>
                                    <div>
                                        <select
                                            id="source-select"
                                            value={selectedSourceId}
                                            onChange={(e) => {
                                                setSelectedSourceId(e.target.value);
                                                setValidationErrors(prev => ({ ...prev, source: undefined }));
                                            }}
                                            className={`block w-full py-1.5 text-xs border ${validationErrors.source ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'} rounded-md`}
                                        >
                                            <option value="">Select a source</option>
                                            {sources.map(source => (
                                                <option key={source.id} value={source.id}>
                                                    {source.name}
                                                </option>
                                            ))}
                                        </select>
                                        {validationErrors.source && (
                                            <p className="mt-1 text-xs text-red-500">{validationErrors.source}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            {/* User Selection */}
                            <div className="mt-3 border-t border-gray-200 pt-3">
                                <label className="block text-xs font-medium text-gray-700 mb-2">Select Users</label>
                                <div className="flex items-center space-x-2">
                                    <div className="relative flex-1">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                            </svg>
                                        </div>
                                        <input
                                            type="text"
                                            placeholder="Search users..."
                                            className="block w-full pl-10 pr-3 py-1.5 text-xs border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                            value={userSearch}
                                            onChange={(e) => setUserSearch(e.target.value)}
                                        />
                                    </div>
                                    <div className="flex items-center">
                                        <button 
                                            onClick={() => handleSelectAllUsers(true)}
                                            className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 bg-blue-50 rounded-l-md border border-blue-100">
                                            Select All
                                        </button>
                                        <button 
                                            onClick={() => handleSelectAllUsers(false)}
                                            className="text-xs text-red-600 hover:text-red-800 px-2 py-1 bg-red-50 rounded-r-md border border-red-100 border-l-0">
                                            Clear
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="max-h-72 overflow-y-auto">
                            {companyUsers.length > 0 ? (
                                <div>
                                    {companyUsers
                                        .filter(user => 
                                            userSearch === '' ||
                                            (user.name?.toLowerCase() || '').includes(userSearch.toLowerCase()) ||
                                            (user.email?.toLowerCase() || '').includes(userSearch.toLowerCase()) ||
                                            (user.role?.toLowerCase() || '').includes(userSearch.toLowerCase())
                                        )
                                        .map(user => (
                                            <div key={user.id} className="flex items-center px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0">
                                                <div className="flex-shrink-0 mr-2">
                                                    {user.photoURL ? (
                                                        <img 
                                                            src={user.photoURL} 
                                                            alt={user.name} 
                                                            className="h-7 w-7 rounded-full"
                                                        />
                                                    ) : (
                                                        <div className="h-7 w-7 rounded-full bg-blue-100 flex items-center justify-center">
                                                            <span className="text-blue-800 font-medium text-xs">
                                                                {user.name ? user.name.split(' ').map(n => n[0]).join('') : user.email ? user.email.charAt(0).toUpperCase() : 'U'}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex-1 min-w-0 leading-tight">
                                                    <div className="font-medium text-xs text-gray-900 truncate">{user.name || 'Unnamed User'}</div>
                                                    <div className="text-xs text-gray-500 truncate">{user.email}</div>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    {user.role && <span className="inline-block px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">{user.role}</span>}
                                                    <div className="flex-shrink-0">
                                                        <input 
                                                            type="checkbox" 
                                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                            checked={!!selectedUsers[user.id]}
                                                            onChange={() => {
                                                                setSelectedUsers(prev => ({
                                                                    ...prev,
                                                                    [user.id]: !prev[user.id]
                                                                }));
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    }
                                    {userSearch && companyUsers.filter(user => 
                                        (user.name?.toLowerCase() || '').includes(userSearch.toLowerCase()) ||
                                        (user.email?.toLowerCase() || '').includes(userSearch.toLowerCase()) ||
                                        (user.role?.toLowerCase() || '').includes(userSearch.toLowerCase())
                                    ).length === 0 && (
                                        <div className="text-center py-4 text-xs text-gray-500">
                                            No users found matching "{userSearch}"
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center py-6">
                                    <svg className="h-8 w-8 text-gray-400 mx-auto mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                    <p className="text-xs text-gray-500">No company users found</p>
                                </div>
                            )}
                        </div>
                        
                        <div className="p-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
                            <div className="text-xs text-gray-500">
                                {Object.keys(selectedUsers).filter(id => selectedUsers[id]).length} users selected
                            </div>
                            <div className="flex space-x-2">
                                <button
                                    onClick={handleCancelUserAssignment}
                                    className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-50 focus:outline-none"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirmUserAssignment}
                                    className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    disabled={Object.keys(selectedUsers).filter(id => selectedUsers[id]).length === 0}
                                >
                                    Assign Users
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Toast notification */}
            {showToast && (
                <Toast 
                    message={toastMessage}
                    isError={isErrorToast}
                    onClose={() => setShowToast(false)}
                    show={showToast}
                />
            )}
        </div>
    );
};

export default MetaAds;